import type {Values} from '@wix/devzai-utils-common';

/**
 * Names of the descriptionLines that doppe attaches to Wix Checkout custom
 * line items in flows that need to bind a paid order to a specific doppe
 * entity (Payment Lock action, paid Download File CTA, etc.). These names
 * are read back from the order's line items on the server to verify which
 * entity an order was purchased for.
 *
 * - `Reference` — machine-matched: holds the entity id (e.g. the Payment Lock
 *   action id, or the Download File CTA's action id) and is compared
 *   verbatim against the expected id by {@link DoppeWixCheckoutOrderMatchStatus}-
 *   producing validators.
 * - `Unlocks` — human-facing context for the buyer: a short label naming the
 *   content that the order unlocks.
 * - `Description` — buyer-facing custom item-type description (e.g. the
 *   shown description for a paid digital download).
 */
export const DoppeWixCheckoutOrderDescriptionLineName = {
    Unlocks: 'Unlocks',
    Reference: 'Reference',
    Description: 'Description',
} as const;

export type DoppeWixCheckoutOrderDescriptionLineName =
    Values<typeof DoppeWixCheckoutOrderDescriptionLineName>;

/**
 * Result of validating an order-driven unlock attempt against a Wix order
 * for a specific doppe entity (a Payment Lock action, a paid Download File
 * CTA action, etc.). Returned by the shared server-side
 * `validateWixCheckoutOrder` helper and consumed by viewer surfaces (Payment
 * Lock widget, Claim Download modal) to pick the right error message.
 *
 * - `Matched` — order found, paid, email matches, `Reference` descriptionLine
 *   equals the expected entity id.
 * - `NotFound` — no order matches the provided order number on the site, or
 *   the request was missing the credentials needed to find one.
 * - `EmailMismatch` — order exists but its buyer email differs from the
 *   provided one (case-insensitive).
 * - `NotPaid` — order exists but is neither marked `PAID` nor `APPROVED`.
 * - `WrongLock` — order exists and matches by email + paid status, but the
 *   `Reference` descriptionLine ties it to a different doppe entity than the
 *   one being unlocked. (Name kept for backwards compatibility with the
 *   Payment Lock surface; semantically "wrong entity binding".)
 */
export const DoppeWixCheckoutOrderMatchStatus = {
    Matched: 'matched',
    NotFound: 'notFound',
    EmailMismatch: 'emailMismatch',
    NotPaid: 'notPaid',
    WrongLock: 'wrongLock',
} as const;

export type DoppeWixCheckoutOrderMatchStatus =
    Values<typeof DoppeWixCheckoutOrderMatchStatus>;
